import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SideDrawer from "../SideDrawer";
import Button from "../Button";
import Input from "../Input";
import OrganizationPicker from "../organization/OrganizationPicker";
import { useUserPermissions } from "../../contexts/UserPermissionsContext";
import { Organization, OrganizationType } from "../../types/Organization";
import { SearchResponseBody, SearchResultItem } from "../../constants/searchCategories";
import { postReq } from "../../utils/request";
import { PriceSheetKind, ProposedPriceLine } from "../../utils/priceList";
import { START_PRICE_LIST_EVENT, handOverPriceList } from "../../utils/priceListHandoff";
import { PRICE_SHEETS, priceSheetsFor } from "../../utils/priceSheets";
import classes from "./priceListReader.module.scss";

/** Where each sheet lives, and the quick-search type that finds its records. */
const SHEET_PAGES: Record<PriceSheetKind, { searchType: string | null; url: (id: number) => string }> = {
    provider: { searchType: null, url: (id) => `/provider/${id}?tab=services` },
    request: { searchType: "missionRequest", url: (id) => `/mission-request/${id}` },
    mission: { searchType: "mission", url: (id) => `/mission/${id}?tab=locations` },
};

/**
 * "Read a price list into…" (#30): opened from Ask's paperclip or the
 * "Upload a price list" search row. The person picks which record's sheet;
 * the sheet page opens with "Add from a price list" holding the file. Picking
 * the record here is free — no AI, no message — and only finds records the
 * person can see (the gated quick search). Only sheets the person can edit
 * with the finance right are offered; nothing shows otherwise.
 */
const PriceListStart: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useUserPermissions() as any;
    const sheets = priceSheetsFor(currentUser?.pages ?? []);
    const picker = useRef<HTMLInputElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [text, setText] = useState<string | undefined>(undefined);
    const [lines, setLines] = useState<ProposedPriceLine[] | undefined>(undefined);
    const [sheet, setSheet] = useState<PriceSheetKind | null>(null);
    const [provider, setProvider] = useState<Organization | null>(null);
    const [keyword, setKeyword] = useState("");
    const [found, setFound] = useState<SearchResultItem[]>([]);
    const [record, setRecord] = useState<SearchResultItem | null>(null);

    useEffect(() => {
        const handleStart = (event: Event) => {
            const detail = (event as CustomEvent<{ file?: File; text?: string; lines?: ProposedPriceLine[] }>).detail ?? {};
            setFile(detail.file ?? null);
            setText(detail.text);
            setLines(detail.lines);
            setProvider(null);
            setSheet(null);
            setKeyword("");
            setFound([]);
            setRecord(null);
            setIsOpen(true);
        };
        window.addEventListener(START_PRICE_LIST_EVENT, handleStart);
        return () => window.removeEventListener(START_PRICE_LIST_EVENT, handleStart);
    }, []);

    const chosenSheet = sheet ?? (sheets.length === 1 ? sheets[0] : null);
    const searchType = chosenSheet ? SHEET_PAGES[chosenSheet].searchType : null;

    // Requests and missions: the gated quick search, filtered to that kind.
    useEffect(() => {
        if (!isOpen || !searchType || keyword.trim().length < 2) {
            setFound([]);
            return undefined;
        }
        let isCurrent = true;
        const timer = setTimeout(async () => {
            const response = await postReq<SearchResponseBody>("extra/quick-search", { text: keyword.trim() }).catch(() => null);
            if (!isCurrent) return;
            setFound(response?.status === 200 ? (response.body?.data ?? []).filter((item) => item.type === searchType).slice(0, 8) : []);
        }, 250);
        return () => {
            isCurrent = false;
            clearTimeout(timer);
        };
    }, [isOpen, searchType, keyword]);

    if (!sheets.length) return null;
    const recordId = chosenSheet === "provider" ? provider?.organizationId ?? null : record ? Number(record.id) : null;

    const openSheet = () => {
        if (!chosenSheet || recordId == null) return;
        handOverPriceList({ sheet: chosenSheet, recordId, file: file ?? undefined, text, lines });
        setIsOpen(false);
        navigate(SHEET_PAGES[chosenSheet].url(recordId));
    };

    const pickSheet = (next: PriceSheetKind) => {
        setSheet(next);
        setRecord(null);
        setKeyword("");
    };

    return (
        <SideDrawer open={isOpen} title="Fill with AI · which price sheet?" onClose={() => setIsOpen(false)} width={560}
            headerExtra={(
                <Button type="button" variant="primary" icon="box-arrow-up-right" disabled={recordId == null} onClick={openSheet}
                    title="Open that sheet with the price list ready to read. You tick the lines; nothing is saved until you Save.">
                    Open the sheet
                </Button>
            )}>
            <div className={classes.body}>
                {sheets.length > 1 && <p className={classes.hint}>Which sheet should the lines go into?</p>}
                {sheets.length > 1 && (
                    <div className={classes.targets} role="group" aria-label="Which sheet">
                        {PRICE_SHEETS.filter((entry) => sheets.includes(entry.sheet)).map((entry) => (
                            <button key={entry.sheet} type="button" className={`btn-unstyled ${classes.target}`} aria-pressed={chosenSheet === entry.sheet}
                                onClick={() => pickSheet(entry.sheet)} title={`Read it into ${entry.label.toLowerCase()}`}>
                                {entry.label}
                            </button>
                        ))}
                    </div>
                )}
                {chosenSheet === "provider" && (
                    <OrganizationPicker value={provider} onChange={setProvider} typeFilter={[OrganizationType.Provider, OrganizationType.Hybrid]}
                        placeholder="Which provider?" showOpenLink={false} id="price-list-provider" />
                )}
                {searchType && (
                    <>
                        <Input type="text" id="price-list-record" label={chosenSheet === "request" ? "Which request? (reference or client)" : "Which mission? (reference or client)"}
                            value={keyword} onChange={(_, value) => { setKeyword(String(value ?? "")); setRecord(null); }}
                            tooltip="Only records you can see are found" />
                        {found.length > 0 && !record && (
                            <div className={classes.results} role="listbox" aria-label="Matching records">
                                {found.map((item) => (
                                    <button key={`${item.type}-${item.id}`} type="button" role="option" aria-selected={false}
                                        className={`btn-unstyled ${classes.result}`} onClick={() => setRecord(item)} title={`Use ${item.referenceNumber || item.name}`}>
                                        <strong>{item.referenceNumber || item.name}</strong>
                                        {item.parent && <span>{item.parent}</span>}
                                    </button>
                                ))}
                            </div>
                        )}
                        {record && (
                            <span className={classes.fileName}>
                                <i className="bi bi-check2-circle" aria-hidden="true" /> {record.referenceNumber || record.name}
                                <Button type="button" variant="ghost" size="xs" icon="x-lg" onClick={() => setRecord(null)} title="Pick another">Change</Button>
                            </span>
                        )}
                    </>
                )}
                <div className={classes.fileRow}>
                    <input ref={picker} type="file" hidden aria-label="Price list file" accept=".xlsx,.xls,.csv,image/*,application/pdf"
                        onChange={(event) => { setFile(event.target.files?.[0] ?? null); event.target.value = ""; }} />
                    <Button type="button" variant="secondary" size="sm" icon="paperclip" onClick={() => picker.current?.click()}
                        title="Excel (.xlsx) or CSV is read on the page; a photo or PDF (handwriting too) is read with AI">
                        {file ? "Change file" : "Choose Excel, photo or PDF"}
                    </Button>
                    {file && <span className={classes.fileName}><i className="bi bi-file-earmark" aria-hidden="true" /> {file.name}</span>}
                </div>
                <p className={classes.hint}>
                    No file? Open the sheet and paste the list there. Picking the record here uses no AI.
                </p>
            </div>
        </SideDrawer>
    );
};

export default PriceListStart;
